import { useMemo, useEffect, useRef, useCallback, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { agent } from '../api/agent';
import { useSessionStore, sessionSelectors } from '../store/sessionStore';
import { Toast } from './Toast';
import { ScoringTable } from './ScoringTable';

export const MashupExplorer = () => {
  const concepts = useSessionStore(sessionSelectors.concepts);
  const synonyms = useSessionStore(sessionSelectors.synonyms).filter((syn) => !syn.dismissed);
  const mashups = useSessionStore(sessionSelectors.mashups);
  const shortlisted = useSessionStore(sessionSelectors.shortlisted);
  const selectedStyles = useSessionStore(sessionSelectors.selectedStyles);
  const addMashups = useSessionStore((state) => state.addMashups);
  const clearMashups = useSessionStore((state) => state.clearMashups);
  const removeMashup = useSessionStore((state) => state.removeMashup);
  const voteMashup = useSessionStore((state) => state.voteMashup);
  const toggleShortlist = useSessionStore((state) => state.toggleShortlist);
  const setDomainStatus = useSessionStore((state) => state.setDomainStatus);
  const setMashupEvaluations = useSessionStore((state) => state.setMashupEvaluations);
  const brief = useSessionStore(sessionSelectors.brief);

  const [manualInput, setManualInput] = useState('');
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);
  const [isScoring, setIsScoring] = useState(false);

  const observerRef = useRef<IntersectionObserver | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const checkingDomains = useRef(new Set<string>());

  const hasInputs = useMemo(
    () => concepts.length > 0 && synonyms.length > 0,
    [concepts.length, synonyms.length]
  );

  const selectedTlds = useSessionStore(sessionSelectors.selectedTlds);
  const showOnlyFavorited = useSessionStore(sessionSelectors.showOnlyFavorited);
  const hideUnavailable = useSessionStore(sessionSelectors.hideUnavailable);
  const setAvailableDomains = useSessionStore((state) => state.setAvailableDomains);
  const toggleTld = useSessionStore((state) => state.toggleTld);
  const toggleShowOnlyFavorited = useSessionStore((state) => state.toggleShowOnlyFavorited);
  const toggleHideUnavailable = useSessionStore((state) => state.toggleHideUnavailable);
  const recheckAllDomains = useSessionStore((state) => state.recheckAllDomains);
  const exportForCollabrador = useSessionStore((state) => state.exportForCollabrador);

  const checkDomain = useCallback(async (mashupId: string, name: string) => {
    if (checkingDomains.current.has(mashupId)) {
      return;
    }
    checkingDomains.current.add(mashupId);
    setDomainStatus(mashupId, 'checking');

    try {
      const response = await agent.searchDomains(name);
      console.log('Search response for', name, ':', response);
      setAvailableDomains(mashupId, response.domains);
    } catch (error) {
      console.error('Domain search error:', error);
      setDomainStatus(mashupId, 'error');
    } finally {
      checkingDomains.current.delete(mashupId);
    }
  }, [setDomainStatus, setAvailableDomains]);

  useEffect(() => {
    mashups.forEach((mashup) => {
      if (mashup.domainStatus === 'unknown') {
        checkDomain(mashup.id, mashup.label);
      }
    });
  }, [mashups, checkDomain]);

  const scoreShortlistedNames = useCallback(async () => {
    if (!showOnlyFavorited || shortlisted.length === 0 || !brief) {
      return;
    }

    const shortlistedMashups = mashups.filter(m => shortlisted.includes(m.id));
    const needsScoring = shortlistedMashups.filter(m => !m.evaluation);

    if (needsScoring.length === 0) {
      return;
    }

    setIsScoring(true);
    try {
      const names = needsScoring.map(m => m.label);
      const response = await agent.scoreNames(brief, names);
      setMashupEvaluations(response.scores.map(score => ({
        name: score.name,
        evaluation: {
          scores: score.scores,
          totalScore: score.totalScore,
          strengths: score.strengths,
          risks: score.risks,
          notes: score.notes
        }
      })));
      setToast({ message: `Scored ${names.length} name${names.length > 1 ? 's' : ''}`, type: 'success' });
    } catch (error) {
      console.error('Scoring error:', error);
      setToast({ message: 'Failed to score names', type: 'error' });
    } finally {
      setIsScoring(false);
    }
  }, [showOnlyFavorited, shortlisted, mashups, brief, setMashupEvaluations]);

  useEffect(() => {
    if (showOnlyFavorited && shortlisted.length > 0) {
      scoreShortlistedNames();
    }
  }, [showOnlyFavorited]);

  const mutation = useMutation({
    mutationFn: () => agent.generateMashups(concepts, synonyms, selectedStyles),
    onSuccess: (data) => {
      addMashups(data.mashups);
    }
  });

  useEffect(() => {
    if (!sentinelRef.current || !hasInputs) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !mutation.isPending && mashups.length > 0) {
          mutation.mutate();
        }
      },
      { threshold: 0.1 }
    );

    observerRef.current.observe(sentinelRef.current);

    return () => {
      observerRef.current?.disconnect();
    };
  }, [hasInputs, mutation, mashups.length]);

  const filteredMashups = useMemo(() => {
    let filtered = mashups;

    if (showOnlyFavorited) {
      filtered = filtered.filter((m) => shortlisted.includes(m.id));
    }

    if (hideUnavailable) {
      filtered = filtered.filter((m) => {
        const availableInSelectedTlds = (m.availableDomains || []).some((d) =>
          selectedTlds.includes(d.tld)
        );
        return availableInSelectedTlds;
      });
    }

    return filtered;
  }, [mashups, shortlisted, showOnlyFavorited, hideUnavailable, selectedTlds]);

  const availableTlds = ['com', 'ai', 'net', 'org'];

  const handleManualAdd = async () => {
    const trimmed = manualInput.trim();
    if (!trimmed) return;

    addMashups([{ label: trimmed, votes: 0, sources: [], availableDomains: [] }]);
    setManualInput('');

    try {
      const response = await agent.searchDomains(trimmed);
      const availableInSelected = response.domains.filter((d) => selectedTlds.includes(d.tld));

      if (availableInSelected.length > 0) {
        const tlds = availableInSelected.map((d) => `.${d.tld}`).join(', ');
        setToast({ message: `${trimmed} available: ${tlds}`, type: 'success' });
      } else {
        setToast({ message: `${trimmed} not available in selected TLDs`, type: 'error' });
      }
    } catch (error) {
      setToast({ message: `Failed to check availability for ${trimmed}`, type: 'error' });
    }
  };

  const handleManualKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleManualAdd();
    }
  };

  const unavailableCount = useMemo(() => {
    return mashups.filter((m) => {
      const availableInSelectedTlds = (m.availableDomains || []).some((d) =>
        selectedTlds.includes(d.tld)
      );
      return !availableInSelectedTlds && m.domainStatus !== 'checking' && m.domainStatus !== 'unknown';
    }).length;
  }, [mashups, selectedTlds]);

  return (
    <div className="panel-section">
      <header className="mashup-header-sticky">
        <div>
          <h2>Mashup Generator</h2>
          <div className="mashup-counts">
            {mashups.length} ideas · {shortlisted.length} favorited
          </div>
        </div>
        <div className="mashup-header-controls">
          <input
            type="text"
            value={manualInput}
            onChange={(e) => setManualInput(e.target.value)}
            onKeyDown={handleManualKeyDown}
            onBlur={handleManualAdd}
            placeholder="Add mashup idea..."
            className="mashup-manual-input"
          />
          <div className="tld-picker">
            {availableTlds.map((tld) => (
              <button
                key={tld}
                type="button"
                className={selectedTlds.includes(tld) ? 'tld-chip active' : 'tld-chip'}
                onClick={() => toggleTld(tld)}
              >
                .{tld}
              </button>
            ))}
          </div>
          <button
            type="button"
            className={showOnlyFavorited ? 'secondary-button active' : 'secondary-button'}
            onClick={() => toggleShowOnlyFavorited()}
          >
            {showOnlyFavorited ? 'Show All' : 'Show Favorited'}
          </button>
          <button
            type="button"
            className={hideUnavailable ? 'secondary-button active' : 'secondary-button'}
            onClick={() => toggleHideUnavailable()}
          >
            {hideUnavailable ? `Show Unavailable (${unavailableCount})` : `Hide Unavailable (${unavailableCount})`}
          </button>
          {mashups.length > 0 && (
            <button
              type="button"
              className="secondary-button"
              onClick={() => recheckAllDomains()}
            >
              Recheck Domains
            </button>
          )}
          {shortlisted.length > 0 && (
            <button
              type="button"
              className="secondary-button"
              onClick={() => {
                exportForCollabrador();
                setToast({ message: 'Saved Collabrador export', type: 'success' });
              }}
              title="Export favorited names as Markdown for AI research with Collabrador"
            >
              Download for Collabrador
            </button>
          )}
          {mashups.length > 0 && !showOnlyFavorited && (
            <button
              type="button"
              className="secondary-button"
              onClick={() => clearMashups()}
            >
              Clear
            </button>
          )}
          <button
            type="button"
            className="primary-button"
            disabled={!hasInputs || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? 'Generating…' : mashups.length > 0 ? 'Generate More' : 'Generate'}
          </button>
        </div>
      </header>

      {filteredMashups.length === 0 ? (
        <p className="empty-state">
          {showOnlyFavorited
            ? 'No favorites yet. Star some ideas to see them here.'
            : 'Surface a handful of synonyms you like, then generate mashups to see creative combos.'}
        </p>
      ) : showOnlyFavorited ? (
        <>
          {isScoring && (
            <div className="scoring-status">
              <p>Evaluating names according to scoring principles...</p>
            </div>
          )}
          <ScoringTable
            mashups={filteredMashups}
            onRemove={removeMashup}
            onToggleShortlist={toggleShortlist}
            shortlisted={shortlisted}
          />
        </>
      ) : (
        <>
          <div className="mashup-grid">
            {filteredMashups.map((mashup) => {
              const isShortlisted = shortlisted.includes(mashup.id);
              const displayName = mashup.label.replace(/\.com$/i, '').replace(/\.(net|org|ai|io)$/i, '');
              const filteredDomains = (mashup.availableDomains || []).filter((d) => selectedTlds.includes(d.tld));
              const computedStatus = mashup.domainStatus === 'checking' ? 'checking' :
                                     mashup.domainStatus === 'error' ? 'error' :
                                     filteredDomains.length > 0 ? 'available' : 'taken';
              const statusClass = `domain-${computedStatus}`;

              return (
                <div
                  key={mashup.id}
                  className={`mashup-card ${isShortlisted ? 'shortlisted' : ''} ${statusClass}`}
                >
                  <button
                    type="button"
                    className={isShortlisted ? 'shortlist-button active' : 'shortlist-button'}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleShortlist(mashup.id);
                    }}
                    title={isShortlisted ? 'Remove from shortlist' : 'Add to shortlist'}
                  >
                    {isShortlisted ? '★' : '☆'}
                  </button>
                  <button
                    type="button"
                    className="remove-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeMashup(mashup.id);
                    }}
                    title="Remove mashup"
                  >
                    ×
                  </button>
                  <h3>{displayName}</h3>
                  <div className="mashup-status">
                    {mashup.domainStatus === 'checking' && <span className="domain-indicator checking">⋯</span>}
                    {filteredDomains.length > 0 && (
                      <div className="available-domains">
                        {filteredDomains.map((domain) => {
                          const hasRegisterUrl = domain.registerURL && domain.registerURL.length > 0;
                          console.log('Domain:', domain.domain, 'Has URL:', hasRegisterUrl, 'URL:', domain.registerURL);
                          return (
                            <a
                              key={domain.domain}
                              href={hasRegisterUrl ? domain.registerURL : '#'}
                              target={hasRegisterUrl ? '_blank' : '_self'}
                              rel="noopener noreferrer"
                              className="domain-pill"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!hasRegisterUrl) {
                                  e.preventDefault();
                                }
                              }}
                              title={hasRegisterUrl ? `Click to register ${domain.domain}` : domain.domain}
                            >
                              .{domain.tld}
                            </a>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {!showOnlyFavorited && (
            <div ref={sentinelRef} style={{ height: '20px', margin: '1rem 0' }}>
              {mutation.isPending && <p className="empty-state">Loading more...</p>}
            </div>
          )}
        </>
      )}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};
